import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { base44 } from "@/api/base44Client";
import { buildInstallResult, detectExtensionInstallEnvironment, validateExtensionPackages } from "@/lib/extensionInstallDetection";

const titles = { chrome: "Install for Chrome", edge: "Install for Microsoft Edge", firefox: "Install for Firefox", safari: "Install for Safari", unknown: "Browser not supported yet" };
const ctas = { chrome: "Install from Chrome Web Store", edge: "Install from Microsoft Edge Add-ons", firefox: "Install Firefox Add-on", safari: "Open Safari Extension App", unknown: "Choose browser manually" };
const platform = { chrome: "Chrome", edge: "Microsoft Edge", firefox: "Firefox", safari: "Safari", unknown: "Unsupported browser" };

export default function MarstickExtensionInstallSDK({ forcedBrowser }) {
  const [detected, setDetected] = React.useState(null);
  React.useEffect(() => {
    const base = detectExtensionInstallEnvironment();
    setDetected(forcedBrowser ? buildInstallResult({ ...base, browser: forcedBrowser }) : base);
  }, [forcedBrowser]);
  if (!detected) return null;
  const validation = validateExtensionPackages()[detected.browser] || null;
  const publicReady = Boolean(detected.installUrl && validation?.ready);
  const track = () => base44.analytics.track({ eventName: "extension_install_click", properties: { browser: detected.browser, method: detected.recommendedInstallMethod } });
  return (
    <Card className="overflow-hidden rounded-[2rem] border-primary/20 bg-white/95 shadow-2xl shadow-ocean/10">
      <CardContent className="p-5 md:p-8">
        <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.22em] text-primary">Detected: {platform[detected.browser]}</p>
            <h2 className="mt-2 text-2xl font-black md:text-4xl">{titles[detected.browser]}</h2>
            <p className="mt-3 max-w-2xl leading-7 text-muted-foreground">{detected.userMessage}</p>
          </div>
          {publicReady ? <Button asChild size="lg" className="rounded-full"><a href={detected.installUrl} onClick={track} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4" />{ctas[detected.browser]}</a></Button> : <Button size="lg" disabled className="rounded-full">Coming soon</Button>}
        </div>
        <div className="mt-5 rounded-2xl border border-border bg-background/70 p-4 text-sm leading-7 text-muted-foreground">
          <p className="font-bold text-foreground">{validation?.ready ? "Store package ready" : "This extension is not publicly available yet."}</p>
          <p>{validation?.reason || "For the best experience, open this page in Chrome, Edge, Firefox, or Safari."}</p>
          <p className="mt-2 flex gap-2"><ShieldCheck className="mt-1 h-4 w-4 shrink-0 text-primary" />Your browser controls extension installation for your safety. Marstick never installs extensions silently.</p>
        </div>
        {detected.isMobile && <Button asChild variant="outline" className="mt-4 rounded-full"><Link to="/install">Install Marstick Weather App instead</Link></Button>}
      </CardContent>
    </Card>
  );
}